import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import { SearchInputProps } from "./search-input-command.types";
import { normalizeString } from "@/utils/normalize-string";

const VIRTUALIZATION_THRESHOLD = 20;
const MAX_POPOVER_HEIGHT = 300;
const MIN_ITEM_HEIGHT = 40;

export const SearchInputCommand = ({
  label,
  optionsList = [],
  selectOption,
  name,
  searchValue = "",
  updateList,
  searchDisable = false,
  inputWidth,
  disabled = false,
  clear = false,
  className = "",
  chevron = false,
  selectedList = [],
  defaultValue = "",
  ...props
}: SearchInputProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [searchTerm, setSearchTerm] = useState("");

  const searchInputRef = useRef<HTMLInputElement>(null);

  const nameKey = name as
    | "status"
    | "cities"
    | "spaces"
    | "neighborhood"
    | "reference";

  const validOptions = useMemo(() => {
    return optionsList
      .filter(
        (item) =>
          item &&
          item.name &&
          item.name.trim() !== "" &&
          typeof item.id === "number",
      )
      .map((item) => ({
        ...item,
        normalizedName: normalizeString(item.name),
      }));
  }, [optionsList, normalizeString]);

  const shouldUseVirtualization = useMemo(() => {
    return validOptions.length > VIRTUALIZATION_THRESHOLD;
  }, [validOptions.length]);

  const displayValue = useMemo(() => {
    return defaultValue || value;
  }, [defaultValue, value]);

  const displayText = useMemo(() => {
    if (selectedList?.length > 0) {
      return selectedList
        .filter((item) => item.name)
        .map((item) => item.name)
        .join(", ");
    }
    if (displayValue && !clear) return displayValue;
    return label || "Selecione uma opção";
  }, [selectedList, displayValue, clear, label]);

  const idealPopoverWidth = useMemo(() => {
    if (validOptions.length === 0) return 300;

    const maxItemLength = Math.max(
      ...validOptions.map((item) => item.name.length),
      displayText.length,
    );

    const contentWidth = Math.max(maxItemLength * 8 + 80, 200);

    return Math.min(Math.max(contentWidth, 200), 600);
  }, [validOptions, displayText]);

  const getItemHeight = useCallback(
    (index: number): number => {
      const item = validOptions[index];
      if (!item) return MIN_ITEM_HEIGHT;

      const textLength = item.name.length;

      if (textLength > 60) {
        return 70;
      } else if (textLength > 40) {
        return 60;
      } else if (textLength > 25) {
        return 50;
      }

      return MIN_ITEM_HEIGHT;
    },
    [validOptions],
  );

  const totalHeight = useMemo(() => {
    if (!shouldUseVirtualization) return 0;

    const calculatedHeight = validOptions.reduce(
      (total, _, index) => total + getItemHeight(index),
      0,
    );

    return Math.min(calculatedHeight, MAX_POPOVER_HEIGHT);
  }, [shouldUseVirtualization, validOptions, getItemHeight]);

  const handleSelect = useCallback(
    (item: { id: number; name: string }) => {
      if (nameKey === "neighborhood") {
        selectOption(nameKey, item);
      } else {
        if (value === item.name) {
          setValue("");
          selectOption(nameKey, { id: 0, name: "" });
        } else {
          setValue(item.name);
          selectOption(nameKey, item);
        }
        setOpen(false);
      }
    },
    [nameKey, value, selectOption],
  );

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      setSearchTerm(searchValue);
      updateList?.(searchValue);
    },
    [updateList],
  );

  useEffect(() => {
    if (nameKey !== "status" && searchValue === "") {
      setValue("");
    }
  }, [nameKey, searchValue]);

  const ItemComponent = useCallback(
    ({
      item,
    }: {
      item: { id: number; name: string; normalizedName: string };
    }) => {
      const isSelected =
        nameKey === "neighborhood"
          ? selectedList?.some((selected) => selected.name === item.name)
          : value === item.name;

      return (
        <CommandItem
          className="p-2 flex gap-2 items-start text-sm border-b border-gray-200 hover:bg-gray-100 cursor-pointer w-full min-h-[40px]"
          value={item.normalizedName}
          onSelect={() => handleSelect(item)}
        >
          {nameKey === "neighborhood" ? (
            <Checkbox
              checked={isSelected}
              className="mt-0.5 h-4 w-4 flex-shrink-0"
            />
          ) : (
            <RadioGroup>
              <RadioGroupItem
                value={item.name}
                checked={isSelected}
                className="mt-0.5 h-4 w-4 flex-shrink-0"
              />
            </RadioGroup>
          )}
          <span className="flex-1 break-words leading-5 text-left">
            {item.name}
          </span>
        </CommandItem>
      );
    },
    [nameKey, selectedList, value, handleSelect],
  );

  const VirtualizedItemRenderer = useCallback(
    ({ index, data, style }: ListChildComponentProps) => {
      const item = data[index];

      if (!item) {
        console.warn(`Item not found at index ${index}`);
        return null;
      }

      return (
        <div style={style} className="w-full">
          <ItemComponent item={item} />
        </div>
      );
    },
    [ItemComponent],
  );

  const renderList = () => {
    if (validOptions.length === 0) {
      return (
        <CommandEmpty className="p-4 text-center text-sm text-gray-500">
          Nenhum item encontrado.
        </CommandEmpty>
      );
    }

    if (shouldUseVirtualization) {
      return (
        <VariableSizeList
          height={totalHeight}
          width="100%"
          itemCount={validOptions.length}
          itemSize={getItemHeight}
          itemData={validOptions}
          overscanCount={5}
        >
          {VirtualizedItemRenderer}
        </VariableSizeList>
      );
    }

    return (
      <div className="max-h-[300px] overflow-y-auto">
        {validOptions.map((item, index) => {
          return <ItemComponent key={`${item.id}-${index}`} item={item} />;
        })}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          type="button"
          className={`
            ${inputWidth || "w-[350px]"}
            ${inputWidth === "w-full" ? "min-w-[140px]" : ""}
            justify-between min-h-[40px] h-auto truncate
            disabled:bg-gray-200 disabled:border-gray-300 disabled:rounded-b px-2
            ${className}
          `}
          disabled={disabled}
          {...(props as any)}
        >
          <span className="truncate">{displayText}</span>
          {chevron && (
            <ChevronDownIcon
              className={`h-4 w-4 ml-2 flex-shrink-0 transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto min-w-[140px] p-0 z-50"
        style={{
          width:
            inputWidth === "w-full"
              ? `max(var(--radix-popover-trigger-width), ${idealPopoverWidth}px)`
              : `${idealPopoverWidth}px`,
          minWidth:
            inputWidth === "w-full"
              ? "var(--radix-popover-trigger-width)"
              : "140px",
          maxWidth: "90vw",
        }}
        align="start"
        side="bottom"
        sideOffset={4}
      >
        <Command
          shouldFilter={true}
          filter={(value, search) => {
            const normalizedValue = normalizeString(value);
            const normalizedSearch = normalizeString(search);

            return normalizedValue.includes(normalizedSearch) ? 1 : 0;
          }}
        >
          {!searchDisable && (
            <CommandInput
              ref={searchInputRef}
              className="p-3 bg-white text-sm w-full border-b border-gray-200 rounded-t-none"
              name={name}
              placeholder="Digite para buscar..."
              value={searchTerm}
              onValueChange={handleSearchChange}
            />
          )}

          <CommandGroup>
            <div className="relative w-full">{renderList()}</div>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
